/**
 * HotspotInfoPanel - Displays hotspot info content in both 2D UI and VR
 */

import { Scene } from '@babylonjs/core/scene';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { Control } from '@babylonjs/gui/2D/controls/control';
import { StackPanel } from '@babylonjs/gui/2D/controls/stackPanel';
import { Button } from '@babylonjs/gui/2D/controls/button';
import { Image } from '@babylonjs/gui/2D/controls/image';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { HotspotConfig } from './UIManager';
import { HotspotComponent } from '../scenes/HotspotComponent';
import Hls from 'hls.js';
import { Logger } from '../utils/Logger';

// Interface for sound introspection - accesses internal BabylonJS AudioV2 properties
// These are undocumented internal properties used for audio playback position tracking
// Cast to this via `as unknown as SoundInternalProperties` to access internal state
interface SoundInternalProperties {
  // Duration properties
  _duration?: number;
  duration?: number;
  _buffer?: { duration?: number };
  buffer?: { duration?: number };
  _audioBuffer?: { duration?: number };
  _source?: {
    _buffer?: { duration?: number };
    buffer?: { duration?: number };
    context?: AudioContext;
    _context?: AudioContext;
  };
  source?: { buffer?: { duration?: number } };

  // Current time / position tracking
  currentTime?: number;
  _currentTime?: number;
  _playbackPosition?: number;

  // Audio context access paths
  _audioContext?: AudioContext;
  _engine?: { _audioContext?: AudioContext };
  engine?: { _audioContext?: AudioContext };

  // Seek methods
  setCurrentTime?(time: number): void;
  seekTo?(time: number): void;
  getBuffer?(): { duration?: number } | null;

  // Debug tracking flag
  _durationLogged?: boolean;
}

const logger = Logger.getLogger('HotspotInfoPanel');

export class HotspotInfoPanel {
  private scene: Scene;
  private hotspotComponent: HotspotComponent;

  // HTML UI elements

  // HTML UI elements
  private htmlPanel: HTMLElement | null = null;

  // VR GUI elements
  private vrPanel: Mesh | null = null;
  private vrTexture: AdvancedDynamicTexture | null = null;

  // Track current hotspot for state management
  private _currentHotspot: HotspotConfig | null = null;
  private isVRMode: boolean = false;

  constructor(scene: Scene, hotspotComponent: HotspotComponent) {
    this.scene = scene;
    this.hotspotComponent = hotspotComponent;
    this.createHTMLPanel();
  }

  /**
   * Get the currently displayed hotspot
   */
  getCurrentHotspot(): HotspotConfig | null {
    return this._currentHotspot;
  }

  /**
   * Create HTML panel for 2D display
   */
  private createHTMLPanel(): void {
    // Create panel container
    this.htmlPanel = document.createElement('div');
    this.htmlPanel.id = 'hotspot-info-panel';
    this.htmlPanel.className = 'ios-modal';
    this.htmlPanel.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1000;
      display: none;
      width: 90%;
      max-width: 500px;
      max-height: calc(100vh - 48px);
      overflow: hidden;
      background: rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(40px);
      -webkit-backdrop-filter: blur(40px);
      border: 1px solid rgba(255, 255, 255, 0.25);
      border-radius: 32px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
      padding: 32px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      animation: fadeIn 0.3s ease-out;
      flex-direction: column;
    `;

    // Add close button
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '×';
    closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      font-size: 28px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      backdrop-filter: blur(10px);
    `;
    
    closeButton.onmouseenter = () => {
      closeButton.style.background = 'rgba(255, 255, 255, 0.25)';
      closeButton.style.transform = 'scale(1.1)';
    };
    closeButton.onmouseleave = () => {
      closeButton.style.background = 'rgba(255, 255, 255, 0.1)';
      closeButton.style.transform = 'scale(1)';
    };
    
    closeButton.onclick = () => this.hide();
    this.htmlPanel.appendChild(closeButton);

    // Add content container
    const contentContainer = document.createElement('div');
    contentContainer.id = 'hotspot-info-content';
    contentContainer.style.cssText = `
      overflow-y: auto;
      flex: 1 1 auto;
      min-height: 0;
      -webkit-overflow-scrolling: touch;
    `;
    this.htmlPanel.appendChild(contentContainer);

    // Add to body
    document.body.appendChild(this.htmlPanel);

    // Add animation style if not exists
    if (!document.getElementById('hotspot-animations')) {
      const style = document.createElement('style');
      style.id = 'hotspot-animations';
      style.innerHTML = `
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, -45%); }
          to { opacity: 1; transform: translate(-50%, -50%); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Create VR panel for VR display
   */
  private createVRPanel(hotspot: HotspotConfig): void {
    // Clean up existing panel
    this.disposeVRPanel();

    // Calculate dynamic height based on which content sections are present
    // Width is fixed at 2 units / 1024px → 512 px/unit
    const panelWidth = 2;
    const pxPerUnit = 512; // 1024px / 2 units
    let heightUnits = 0.4; // Base padding/margins
    if (hotspot.infoTitle) heightUnits += 0.15;
    if (hotspot.infoImageUrl) heightUnits += 0.6;
    if (hotspot.infoDescription) heightUnits += 0.5;
    if (hotspot.audioUrl && (hotspot.type === 'both' || hotspot.type === 'audio')) heightUnits += 0.25;
    heightUnits += 0.2; // Close button
    const textureHeight = Math.round(heightUnits * pxPerUnit);

    // Create plane with dynamic height, double-sided for VR stereo
    this.vrPanel = MeshBuilder.CreatePlane(
      'hotspot-vr-panel',
      { width: panelWidth, height: heightUnits, sideOrientation: Mesh.DOUBLESIDE },
      this.scene
    );
    this.vrPanel.renderingGroupId = 1;
    this.vrPanel.isPickable = true;
    this.vrPanel.billboardMode = Mesh.BILLBOARDMODE_Y;

    // Position in front of camera
    const camera = this.scene.activeCamera;
    if (camera) {
      const forward = camera.getForwardRay().direction;
      const position = camera.position.add(forward.scale(2));
      this.vrPanel.position = position;
    } else {
      this.vrPanel.position = new Vector3(0, 1.5, -2);
    }

    // Create texture for panel with proportional height
    this.vrTexture = AdvancedDynamicTexture.CreateForMesh(this.vrPanel, 1024, textureHeight);

    // Create background with iOS liquid glass effect
    const background = new Rectangle('info-background');
    background.width = '100%';
    background.height = '100%';
    background.thickness = 2;
    background.background = 'rgba(255, 255, 255, 0.15)';
    background.color = 'rgba(255, 255, 255, 0.3)';
    background.cornerRadius = 24;
    background.shadowBlur = 40;
    background.shadowColor = 'rgba(0, 0, 0, 0.25)';
    background.shadowOffsetY = 12;
    this.vrTexture.addControl(background);

    // Create content container
    const contentPanel = new StackPanel('info-content');
    contentPanel.width = '90%';
    contentPanel.height = '90%';
    contentPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    contentPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    background.addControl(contentPanel);

    // Track used height in pixels for dynamic description sizing
    let usedPx = 0;

    // Add title
    if (hotspot.infoTitle) {
      const title = new TextBlock('info-title', hotspot.infoTitle);
      title.height = '60px';
      title.fontSize = 32;
      title.color = 'white';
      title.fontWeight = '700';
      title.textWrapping = true;
      title.paddingBottom = '20px';
      contentPanel.addControl(title);
      usedPx += 80; // 60px + 20px padding
    }

    // Add image if available
    if (hotspot.infoImageUrl) {
      const image = new Image('info-image', hotspot.infoImageUrl);
      image.width = '100%';
      image.height = '300px';
      image.stretch = Image.STRETCH_UNIFORM;
      image.paddingBottom = '20px';
      contentPanel.addControl(image);
      usedPx += 320; // 300px + 20px padding
    }

    // Add description with dynamic height
    if (hotspot.infoDescription) {
      const availablePx = Math.round(textureHeight * 0.9) - usedPx - 100; // reserve 100px for close btn + audio
      const descHeight = Math.max(60, Math.min(availablePx, 250));
      const description = new TextBlock('info-description', hotspot.infoDescription);
      description.height = `${descHeight}px`;
      description.fontSize = 20;
      description.color = 'rgba(255, 255, 255, 0.9)';
      description.textWrapping = true;
      description.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
      contentPanel.addControl(description);
    }

    // Add Audio Controls (VR)
    if (hotspot.audioUrl && (hotspot.type === 'both' || hotspot.type === 'audio')) {
      const audioPanel = new StackPanel('audio-panel');
      audioPanel.height = '100px';
      audioPanel.paddingTop = '20px';
      audioPanel.isVertical = false;
      audioPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      contentPanel.addControl(audioPanel);

      const playBtn = Button.CreateSimpleButton('play-btn', this.hotspotComponent.isAudioPlaying(hotspot.id) ? '❚❚  Pause' : '▸  Play');
      playBtn.width = '180px';
      playBtn.height = '60px';
      playBtn.color = 'white';
      playBtn.background = 'rgba(255, 255, 255, 0.2)';
      playBtn.cornerRadius = 30;
      playBtn.fontSize = 22;
      playBtn.fontWeight = '600';
      playBtn.onPointerClickObservable.add(() => {
        this.hotspotComponent.playAudio(hotspot.id);
        const isPlaying = this.hotspotComponent.isAudioPlaying(hotspot.id);
        playBtn.textBlock!.text = isPlaying ? '❚❚  Pause' : '▸  Play';
      });
      audioPanel.addControl(playBtn);
    }

    // Add close button with iOS styling
    const closeBtn = Button.CreateSimpleButton('close-btn', 'Close');
    closeBtn.width = '200px';
    closeBtn.height = '60px';
    closeBtn.color = '#1C1C1E';
    closeBtn.background = 'rgba(255, 255, 255, 0.95)';
    closeBtn.cornerRadius = 14;
    closeBtn.fontSize = 24;
    closeBtn.fontWeight = '600';
    closeBtn.paddingTop = '20px';
    closeBtn.shadowBlur = 8;
    closeBtn.shadowColor = 'rgba(0, 0, 0, 0.15)';
    closeBtn.shadowOffsetY = 4;
    closeBtn.onPointerEnterObservable.add(() => {
      closeBtn.background = 'white';
      closeBtn.shadowBlur = 12;
      closeBtn.shadowOffsetY = 6;
    });
    closeBtn.onPointerOutObservable.add(() => {
      closeBtn.background = 'rgba(255, 255, 255, 0.95)';
      closeBtn.shadowBlur = 8;
      closeBtn.shadowOffsetY = 4;
    });
    closeBtn.onPointerClickObservable.add(() => {
      this.hide();
    });
    contentPanel.addControl(closeBtn);
  }

  /**
   * Show info panel for a hotspot
   */
  show(hotspot: HotspotConfig, isVR: boolean = false): void {
    // Stop audio from the previous hotspot if one was open
    if (this._currentHotspot && this._currentHotspot.id !== hotspot.id
        && this._currentHotspot.audioUrl
        && this.hotspotComponent.isAudioPlaying(this._currentHotspot.id)) {
      this.hotspotComponent.stopAudio(this._currentHotspot.id);
    }

    this._currentHotspot = hotspot;
    this.isVRMode = isVR;

    if (isVR) {
      this.showVRPanel(hotspot);
    } else {
      this.showHTMLPanel(hotspot);
    }
  }

  /**
   * Show HTML panel
   */
  private showHTMLPanel(hotspot: HotspotConfig): void {
    if (!this.htmlPanel) return;

    const contentContainer = this.htmlPanel.querySelector('#hotspot-info-content');
    if (!contentContainer) return;

    // Clear previous content
    contentContainer.innerHTML = '';

    // Add title
    if (hotspot.infoTitle) {
      const title = document.createElement('h2');
      title.textContent = hotspot.infoTitle;
      title.style.cssText = `
        margin: 0 0 16px 0;
        font-size: 32px;
        font-weight: 700;
        color: white;
        letter-spacing: -0.5px;
        text-shadow: 0 2px 10px rgba(0,0,0,0.3);
      `;
      contentContainer.appendChild(title);
    }

    // Determine which content type to show (only show ONE, not all)
    // Use infoContentType if set, otherwise fallback to first available
    const hasVideo = hotspot.videoUrl || hotspot.videoMp4Url;
    const contentType = hotspot.infoContentType ||
      (hasVideo ? 'video' : hotspot.iframeUrl ? 'iframe' : 'image');

    // Add image (only if image content type selected)
    if (contentType === 'image' && hotspot.infoImageUrl) {
      const img = document.createElement('img');
      img.src = hotspot.infoImageUrl;
      img.style.cssText = `
        width: 100%;
        max-height: 300px;
        object-fit: cover;
        border-radius: 20px;
        margin-bottom: 24px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.3);
        border: 1px solid rgba(255,255,255,0.1);
      `;
      contentContainer.appendChild(img);
    }

    // Add video (only if video content type selected)
    if (contentType === 'video' && hasVideo) {
      const videoContainer = document.createElement('div');
      videoContainer.style.cssText = `
        width: 100%;
        margin-bottom: 24px;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 8px 24px rgba(0,0,0,0.3);
        border: 1px solid rgba(255,255,255,0.1);
      `;

      const video = document.createElement('video');
      video.controls = true;
      video.style.width = '100%';
      video.style.display = 'block';

      // Determine video source - prefer HLS for streaming, fallback to MP4
      const hlsUrl = hotspot.videoUrl;
      const mp4Url = hotspot.videoMp4Url;
      const isHlsUrl = hlsUrl && hlsUrl.includes('.m3u8');

      if (isHlsUrl && Hls.isSupported()) {
        // Use hls.js for HLS streaming (most browsers)
        const hls = new Hls();
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal && mp4Url) {
            logger.warn('HLS error, falling back to MP4');
            hls.destroy();
            video.src = mp4Url;
          }
        });
      } else if (isHlsUrl && video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari supports HLS natively
        video.src = hlsUrl;
        video.addEventListener('error', () => {
          if (mp4Url) {
            logger.warn('HLS error, falling back to MP4');
            video.src = mp4Url;
          }
        });
      } else if (mp4Url) {
        // Use MP4 directly (either no HLS URL or browser doesn't support HLS)
        video.src = mp4Url;
      } else if (hlsUrl) {
        // Last resort: try the URL directly (might be MP4 despite being in videoUrl field)
        video.src = hlsUrl;
      }

      videoContainer.appendChild(video);
      contentContainer.appendChild(videoContainer);
    }

    // Add iframe (only if iframe content type selected)
    if (contentType === 'iframe' && hotspot.iframeUrl) {
      const iframeContainer = document.createElement('div');
      iframeContainer.style.cssText = `
        width: 100%;
        height: 300px;
        margin-bottom: 24px;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 8px 24px rgba(0,0,0,0.3);
        border: 1px solid rgba(255,255,255,0.1);
        background: white;
      `;

      const iframe = document.createElement('iframe');
      iframe.src = hotspot.iframeUrl;
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = 'none';

      iframeContainer.appendChild(iframe);
      contentContainer.appendChild(iframeContainer);
    }

    // Add description
    if (hotspot.infoDescription) {
      const description = document.createElement('p');
      description.textContent = hotspot.infoDescription;
      description.style.cssText = `
        margin: 0 0 24px 0;
        font-size: 18px;
        line-height: 1.6;
        color: rgba(255, 255, 255, 0.9);
        font-weight: 400;
      `;
      contentContainer.appendChild(description);
    }

    // Add link button
    if (hotspot.linkUrl) {
      const linkBtn = document.createElement('a');
      linkBtn.href = hotspot.linkUrl;
      linkBtn.target = '_blank';
      linkBtn.textContent = hotspot.linkText || 'Learn More';
      linkBtn.style.cssText = `
        display: block;
        width: auto;
        padding: 16px;
        background: white;
        color: black;
        text-decoration: none;
        text-align: center;
        font-weight: 600;
        font-size: 18px;
        border-radius: 16px;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        box-sizing: border-box;
      `;
      
      linkBtn.onmouseenter = () => {
        linkBtn.style.background = 'rgba(255, 255, 255, 0.95)';
        linkBtn.style.boxShadow = '0 5px 15px rgba(0,0,0,0.2)';
      };
      linkBtn.onmouseleave = () => {
        linkBtn.style.background = 'white';
        linkBtn.style.boxShadow = 'none';
      };
      
      contentContainer.appendChild(linkBtn);
    }

    // Add Audio Controls (for 'both' type or if audioUrl exists)
    if (hotspot.audioUrl && (hotspot.type === 'both' || hotspot.type === 'audio')) {
      const audioContainer = document.createElement('div');
      audioContainer.style.cssText = `
        margin-top: 24px;
        padding: 16px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 16px;
        display: flex;
        align-items: center;
        gap: 16px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        cursor: pointer;
      `;

      // SVG Icons
      const playSvg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>`;
      const pauseSvg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>`;

      // Play/Pause Button
      const playBtn = document.createElement('button');
      const isPlaying = this.hotspotComponent.isAudioPlaying(hotspot.id);

      playBtn.innerHTML = isPlaying ? pauseSvg : playSvg;
      playBtn.style.cssText = `
        width: 44px;
        height: 44px;
        min-width: 44px;
        border-radius: 50%;
        border: none;
        background: white;
        color: black;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.1s ease, background 0.15s ease;
      `;

      playBtn.onmouseenter = () => playBtn.style.background = 'rgba(255,255,255,0.9)';
      playBtn.onmouseleave = () => playBtn.style.background = 'white';
      playBtn.onmousedown = () => playBtn.style.transform = 'scale(0.95)';
      playBtn.onmouseup = () => playBtn.style.transform = 'scale(1)';

      // Player info section
      const playerInfo = document.createElement('div');
      playerInfo.style.cssText = `flex: 1; display: flex; flex-direction: column; gap: 8px; min-width: 0;`;

      // Title
      const titleEl = document.createElement('div');
      titleEl.style.cssText = `font-weight: 600; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
      titleEl.textContent = hotspot.audioTitle || 'Audio';

      // Progress bar container
      const progressContainer = document.createElement('div');
      progressContainer.style.cssText = `
        width: 100%;
        height: 6px;
        background: rgba(255,255,255,0.2);
        border-radius: 3px;
        cursor: pointer;
        overflow: hidden;
      `;

      const progressBar = document.createElement('div');
      progressBar.style.cssText = `
        width: 0%;
        height: 100%;
        background: white;
        border-radius: 3px;
        transition: width 0.1s linear;
      `;
      progressContainer.appendChild(progressBar);

      // Time display
      const timeDisplay = document.createElement('div');
      timeDisplay.style.cssText = `font-size: 11px; opacity: 0.7; font-variant-numeric: tabular-nums;`;
      timeDisplay.textContent = '0:00 / 0:00';

      playerInfo.appendChild(titleEl);
      playerInfo.appendChild(progressContainer);
      playerInfo.appendChild(timeDisplay);

      // Helper to get duration from Babylon.js sound (AudioEngineV2 StaticSound)
      const getSoundDuration = (sound: SoundInternalProperties): number => {
        // Debug: Log sound object structure once
        if (!sound._durationLogged) {
          logger.debug('Sound object keys:', Object.keys(sound));
          logger.debug('Sound object:', sound);
          sound._durationLogged = true;
        }

        // AudioEngineV2 StaticSound stores buffer in _source._buffer or similar
        // Try various property paths for AudioEngineV2
        if (sound._source?._buffer?.duration !== undefined) return sound._source._buffer.duration;
        if (sound._source?.buffer?.duration !== undefined) return sound._source.buffer.duration;
        if (sound._buffer?.duration !== undefined) return sound._buffer.duration;
        if (sound.buffer?.duration !== undefined) return sound.buffer.duration;
        if (sound._audioBuffer?.duration !== undefined) return sound._audioBuffer.duration;
        if (sound._duration !== undefined) return sound._duration;
        if (sound.duration !== undefined) return sound.duration;

        // Try getting through the source node's buffer
        if (sound.source?.buffer?.duration !== undefined) return sound.source.buffer.duration;

        // AudioEngineV2 might expose it through getBuffer() method
        if (typeof sound.getBuffer === 'function') {
          const buffer = sound.getBuffer();
          if (buffer?.duration !== undefined) return buffer.duration;
        }

        return 0;
      };

      // Track playback start time and position for current time calculation
      let playbackStartTime = 0;
      const playbackStartOffset = 0;
      let usePerformanceNow = false; // Flag to indicate we're using performance.now() fallback

      // Helper to get current time from Babylon.js sound (AudioEngineV2 StaticSound)
      const getSoundCurrentTime = (sound: SoundInternalProperties): number => {
        // AudioEngineV2 StaticSound might track position differently
        // Try various property paths
        if (typeof sound.currentTime === 'number' && sound.currentTime > 0) return sound.currentTime;
        if (typeof sound._currentTime === 'number' && sound._currentTime > 0) return sound._currentTime;
        if (typeof sound._playbackPosition === 'number') return sound._playbackPosition;

        // If we have a playback start time, calculate elapsed time
        if (playbackStartTime > 0) {
          if (usePerformanceNow) {
            // Using performance.now() based timing
            const elapsed = (performance.now() / 1000) - playbackStartTime + playbackStartOffset;
            return Math.max(0, elapsed);
          }

          // Try to get from the source's context currentTime relative to start
          if (sound._source?.context) {
            const ctx = sound._source.context;
            const elapsed = ctx.currentTime - playbackStartTime + playbackStartOffset;
            return Math.max(0, elapsed);
          }

          // Try getting the audio context from various paths
          const audioContext = sound._audioContext || sound._engine?._audioContext ||
                             sound._source?._context || sound.engine?._audioContext;
          if (audioContext) {
            const elapsed = audioContext.currentTime - playbackStartTime + playbackStartOffset;
            return Math.max(0, elapsed);
          }
        }

        return 0;
      };

      // Update progress bar periodically
      let progressInterval: number | null = null;
      const updateProgress = () => {
        const isStillPlaying = this.hotspotComponent.isAudioPlaying(hotspot.id);
        const sound = this.hotspotComponent.getSound(hotspot.id);
        if (sound) {
          const soundInternal = sound as unknown as SoundInternalProperties;
          const duration = getSoundDuration(soundInternal);
          const current = Math.min(getSoundCurrentTime(soundInternal), duration);

          if (duration > 0) {
            const percent = Math.min((current / duration) * 100, 100);
            progressBar.style.width = `${percent}%`;

            const formatTime = (sec: number) => {
              const m = Math.floor(sec / 60);
              const s = Math.floor(sec % 60);
              return `${m}:${s.toString().padStart(2, '0')}`;
            };
            timeDisplay.textContent = `${formatTime(current)} / ${formatTime(duration)}`;
          }
        }

        // When playback ends, reset to start so user can tap play again
        if (!isStillPlaying && progressInterval) {
          clearInterval(progressInterval);
          progressInterval = null;
          playBtn.innerHTML = playSvg;
          progressBar.style.width = '0%';
          playbackStartTime = 0;
          if (sound) {
            const soundInternal = sound as unknown as SoundInternalProperties;
            const duration = getSoundDuration(soundInternal);
            const formatTime = (sec: number) => {
              const m = Math.floor(sec / 60);
              const s = Math.floor(sec % 60);
              return `${m}:${s.toString().padStart(2, '0')}`;
            };
            timeDisplay.textContent = `0:00 / ${formatTime(duration)}`;
          }
        }
      };

      // Click to seek (may not work with all audio engines)
      progressContainer.onclick = (e) => {
        e.stopPropagation();
        const rect = progressContainer.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const sound = this.hotspotComponent.getSound(hotspot.id);
        const soundInternal = sound ? sound as unknown as SoundInternalProperties : null;
        const duration = soundInternal ? getSoundDuration(soundInternal) : 0;

        if (soundInternal && duration > 0) {
          const seekTime = percent * duration;
          // Try various seek methods
          if (typeof soundInternal.setCurrentTime === 'function') {
            soundInternal.setCurrentTime(seekTime);
          } else if (typeof soundInternal.seekTo === 'function') {
            soundInternal.seekTo(seekTime);
          } else if (soundInternal.currentTime !== undefined) {
            soundInternal.currentTime = seekTime;
          }
          progressBar.style.width = `${percent * 100}%`;
        }
      };

      // Shared play/pause toggle — called by tapping anywhere on the audio container
      const togglePlayPause = () => {
        this.hotspotComponent.playAudio(hotspot.id);
        const playing = this.hotspotComponent.isAudioPlaying(hotspot.id);
        playBtn.innerHTML = playing ? pauseSvg : playSvg;

        if (playing) {
          const sound = this.hotspotComponent.getSound(hotspot.id) as unknown as SoundInternalProperties | null;
          if (sound) {
            const audioContext = sound._audioContext || sound._engine?._audioContext ||
                               sound._source?.context || sound.engine?._audioContext ||
                               (sound._source?._context);
            if (audioContext) {
              playbackStartTime = audioContext.currentTime;
              usePerformanceNow = false;
            } else {
              playbackStartTime = performance.now() / 1000;
              usePerformanceNow = true;
            }
          }
          progressInterval = window.setInterval(updateProgress, 100);
        } else if (progressInterval) {
          clearInterval(progressInterval);
          progressInterval = null;
        }
      };

      // Start interval if already playing (panel opened while audio was already playing)
      if (isPlaying) {
        const sound = this.hotspotComponent.getSound(hotspot.id) as unknown as SoundInternalProperties | null;
        if (sound) {
          const audioContext = sound._audioContext || sound._engine?._audioContext ||
                             sound._source?.context || sound.engine?._audioContext ||
                             (sound._source?._context);
          if (audioContext) {
            playbackStartTime = audioContext.currentTime;
            usePerformanceNow = false;
          } else {
            playbackStartTime = performance.now() / 1000;
            usePerformanceNow = true;
          }
        }
        progressInterval = window.setInterval(updateProgress, 100);
      }

      // Tap anywhere on audio container to toggle play/pause
      // Only the progress bar stops propagation (for seeking)
      audioContainer.onclick = () => {
        togglePlayPause();
      };

      audioContainer.appendChild(playBtn);
      audioContainer.appendChild(playerInfo);
      contentContainer.appendChild(audioContainer);
    }

    // Show panel (flex for column layout with scrollable content)
    this.htmlPanel.style.display = 'flex';
  }

  /**
   * Show VR panel
   */
  private showVRPanel(hotspot: HotspotConfig): void {
    this.createVRPanel(hotspot);
  }

  /**
   * Hide info panel
   */
  hide(): void {
    // Stop audio if playing for this hotspot
    if (this._currentHotspot?.audioUrl && this.hotspotComponent.isAudioPlaying(this._currentHotspot.id)) {
      this.hotspotComponent.stopAudio(this._currentHotspot.id);
    }

    // Reset hover state on the hotspot (on mobile there's no pointerOut to clear it)
    if (this._currentHotspot) {
      this.hotspotComponent.resetHoverState(this._currentHotspot.id);
    }

    // Hide HTML panel
    if (this.htmlPanel) {
      this.htmlPanel.style.display = 'none';
    }

    // Dispose VR panel
    this.disposeVRPanel();

    this._currentHotspot = null;
  }

  /**
   * Check if panel is currently visible
   */
  isVisible(): boolean {
    if (this.isVRMode) {
      return this.vrPanel !== null;
    } else {
      return this.htmlPanel?.style.display === 'flex';
    }
  }

  /**
   * Dispose VR panel
   */
  private disposeVRPanel(): void {
    if (this.vrTexture) {
      this.vrTexture.dispose();
      this.vrTexture = null;
    }

    if (this.vrPanel) {
      this.vrPanel.dispose();
      this.vrPanel = null;
    }
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    // Remove HTML panel
    if (this.htmlPanel) {
      this.htmlPanel.remove();
      this.htmlPanel = null;
    }

    // Dispose VR panel
    this.disposeVRPanel();

    this._currentHotspot = null;
  }
}
